import hyperid from 'hyperid';
import ClientCookies from 'js-cookie';

export const CHIME_SESSION_COOKIE_KEY = 'chime_session';
const SESSION_TIMEOUT_MINUTES = 30;
export const SESSION_TIMEOUT_MS = SESSION_TIMEOUT_MINUTES * 60 * 1000;

type SessionData = {
	id: string;
	end_ts: number;
};

export type SessionManagerConfig = {
	onNewSession: (session: SessionData) => void;
};

export class SessionManager {
	private config: SessionManagerConfig;

	constructor(config: SessionManagerConfig) {
		this.config = config;
	}

	private parseSessionCookie(): SessionData | null {
		const cookie = ClientCookies.get(CHIME_SESSION_COOKIE_KEY);
		if (!cookie) return null;

		try {
			const [id, end_ts] = cookie.split('&').map(part => part.split('=')[1]);
			const parsedEndTs = parseInt(end_ts);

			if (isNaN(parsedEndTs) || parsedEndTs < 0 || parsedEndTs > Date.now() + SESSION_TIMEOUT_MS) {
				throw new Error('Invalid session cookie');
			}

			return {
				id,
				end_ts: parsedEndTs,
			};
		} catch (error) {
			console.error('Error parsing session cookie:', { error, cookie });
			return null;
		}
	}

	private createAndUpdateNewSession(): SessionData {
		const session = {
			id: hyperid().uuid,
			end_ts: Date.now() + SESSION_TIMEOUT_MS,
		};

		// Send session start event
		this.config.onNewSession(session);

		this.saveSessionToCookie(session);
		return session;
	}

	private saveSessionToCookie(session: SessionData): void {
		ClientCookies.set(CHIME_SESSION_COOKIE_KEY, `id=${session.id}&end_ts=${session.end_ts}`);
	}

	public getAndUpdateSession(): string {
		const currentSession = this.parseSessionCookie();

		// If session is valid, extend it
		if (currentSession && currentSession.end_ts > Date.now()) {
			const updatedSession = {
				...currentSession,
				end_ts: Date.now() + SESSION_TIMEOUT_MS,
			};
			this.saveSessionToCookie(updatedSession);
			return currentSession.id;
		}

		// Create new session if no valid session is found
		const newSession = this.createAndUpdateNewSession();
		return newSession.id;
	}
}
